/**
 * @file UserBillingBadge.tsx
 * @brief Compact plan vs pay-as-you-go cell with minute-pool meter.
 *
 * @details
 * Table-friendly: plan chip + thin brand progress bar + used/left.
 * Priority (ADR-020): Enterprise seated → personal seat → company
 * Enterprise not seated → pay as you go. (27-07-2026)
 *
 * @references
 * - BACKEND/src/features/admin/service.js (getAllUsers)
 * - docs/product/subscriptions.md
 *
 * @author DES
 * @date 27-07-2026
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";

function planLabel(code: string | null | undefined): string {
  if (!code) return "Basic";
  const c = String(code).toLowerCase();
  if (c === "starter") return "Starter";
  if (c === "pro") return "Pro";
  if (c === "business") return "Business";
  if (c === "enterprise") return "Enterprise";
  return c.charAt(0).toUpperCase() + c.slice(1);
}

function fmtMin(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

export function UserBillingBadge({
  planCode,
  status,
  cancelAtPeriodEnd,
  periodEnd,
  usedMinutes,
  includedMinutes,
  remainingMinutes,
  enterpriseActive,
  enterpriseSeated,
  enterpriseUsedMinutes,
  enterpriseIncludedMinutes,
  enterprisePeriodEnd,
}: {
  planCode?: string | null;
  status?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  periodEnd?: string | null;
  usedMinutes?: number | null;
  includedMinutes?: number | null;
  remainingMinutes?: number | null;
  enterpriseActive?: boolean | null;
  enterpriseSeated?: boolean | null;
  enterpriseUsedMinutes?: number | null;
  enterpriseIncludedMinutes?: number | null;
  enterprisePeriodEnd?: string | null;
}) {
  // Company Enterprise seated wins over personal Stripe seat (consume order).
  if (enterpriseActive && enterpriseSeated) {
    const included = Number(enterpriseIncludedMinutes ?? 0);
    const used = Number(enterpriseUsedMinutes ?? 0);
    const remaining = Math.max(0, included - used);
    const hasPool = included > 0;
    const pct = hasPool
      ? Math.min(100, Math.round((used / included) * 100))
      : 0;
    const nearCap = hasPool && pct >= 85;
    const tip = [
      "Enterprise · shared company pool",
      hasPool
        ? `${fmtMin(used)} used · ${fmtMin(remaining)} left of ${fmtMin(included)}`
        : null,
      enterprisePeriodEnd
        ? `Until ${new Date(enterprisePeriodEnd).toLocaleDateString()}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "w-[148px] rounded-lg border border-border/70 bg-card px-2.5 py-2",
                "text-left shadow-[0_1px_0_rgba(0,0,0,0.03)]",
                "transition-colors hover:border-brand/30 hover:bg-brand-soft/30",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-foreground">
                  Enterprise
                </span>
                <span className="rounded bg-brand-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand">
                  Shared
                </span>
              </div>
              {hasPool ? (
                <>
                  <div
                    className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Enterprise shared minute pool"
                  >
                    <div
                      className={cn(
                        "h-full rounded-full transition-[width]",
                        nearCap ? "bg-warning" : "bg-brand",
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="mt-1.5 flex items-baseline justify-between gap-2">
                    <span className="text-[11px] tabular-nums text-foreground/80">
                      <span className="font-semibold text-foreground">
                        {fmtMin(used)}
                      </span>
                      <span className="text-muted-foreground">
                        {" "}
                        / {fmtMin(included)}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "text-[10px] font-medium tabular-nums",
                        nearCap ? "text-warning" : "text-muted-foreground",
                      )}
                    >
                      {fmtMin(remaining)} left
                    </span>
                  </div>
                </>
              ) : (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Seat assigned
                </p>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {tip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (enterpriseActive && !enterpriseSeated) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                // Visible on a grey row, like the Pay as you go pill (01-10-2026).
                "inline-flex flex-col gap-0.5 rounded-xl border border-border",
                "bg-background px-2.5 py-1 text-left",
              )}
            >
              <span className="text-[11px] font-semibold text-foreground">
                Enterprise
              </span>
              <span className="text-[10px] font-medium text-muted-foreground">
                Not seated · PAYG
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            Company has Enterprise, but this user has no seat yet. MI + Notes
            stay pay as you go until a seat is assigned (Employees, or Edit
            user → Enterprise seat).
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const onPlan = Boolean(planCode);
  const label = onPlan ? planLabel(planCode) : "Basic";
  const included = Number(includedMinutes ?? 0);
  const used = Number(usedMinutes ?? 0);
  const remaining =
    remainingMinutes != null
      ? Number(remainingMinutes)
      : Math.max(0, included - used);
  const hasPool = onPlan && included > 0;
  const pct = hasPool
    ? Math.min(100, Math.round((used / included) * 100))
    : 0;
  const nearCap = hasPool && pct >= 85;

  const tip = onPlan
    ? [
        `${label} seat`,
        hasPool
          ? `${fmtMin(used)} used · ${fmtMin(remaining)} left of ${fmtMin(included)}`
          : null,
        status ? `Status: ${status}` : null,
        cancelAtPeriodEnd
          ? periodEnd
            ? `Cancels ${new Date(periodEnd).toLocaleDateString()}`
            : "Cancels at period end"
          : periodEnd
            ? `Renews ${new Date(periodEnd).toLocaleDateString()}`
            : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Pay as you go, no monthly minute pool";

  if (!onPlan) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                // Fully round, and visible on a grey row (DES 01-10-2026).
                "inline-flex items-center rounded-full",
                "bg-background px-2.5 py-0.5 text-[11px] font-semibold text-foreground",
              )}
            >
              Pay as you go
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{tip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "w-[148px] rounded-lg border border-border/70 bg-card px-2.5 py-2",
              "text-left shadow-[0_1px_0_rgba(0,0,0,0.03)]",
              "transition-colors hover:border-brand/30 hover:bg-brand-soft/30",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-foreground">
                {label}
              </span>
              {cancelAtPeriodEnd ? (
                <span className="rounded bg-warning-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">
                  Ends
                </span>
              ) : (
                <span className="rounded bg-brand-soft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand">
                  Seat
                </span>
              )}
            </div>

            {hasPool ? (
              <>
                <div
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${label} minute pool`}
                >
                  <div
                    className={cn(
                      "h-full rounded-full transition-[width]",
                      nearCap ? "bg-warning" : "bg-brand",
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-baseline justify-between gap-2">
                  <span className="text-[11px] tabular-nums text-foreground/80">
                    <span className="font-semibold text-foreground">
                      {fmtMin(used)}
                    </span>
                    <span className="text-muted-foreground">
                      {" "}
                      / {fmtMin(included)}
                    </span>
                  </span>
                  <span
                    className={cn(
                      "text-[10px] font-medium tabular-nums",
                      nearCap ? "text-warning" : "text-muted-foreground",
                    )}
                  >
                    {fmtMin(remaining)} left
                  </span>
                </div>
              </>
            ) : (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Pool not configured
              </p>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
